import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppId } from '../context/AppIdContext';
import '../style/speakSearch.css';
import Header from '../component/header'; // ADDED HEADER IMPORT

const BASE_URL = process.env.REACT_APP_BASE_URL;

const StarVolunteer = () => {
    const [volunteers, setVolunteers] = useState([]);
    const [loading, setLoading] = useState(false);
    const { appId, language } = useAppId();

    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            // Page titles
            title: "Star Volunteers",
            subtitle: "Top performing karyakartas based on total points",
            
            // Messages
            loading: "Loading Leaderboard...",
            noVolunteers: "No volunteers found.",
            
            // Card labels
            totalPoint: "Total Point:",
            volunteer: "Volunteer",
            call: "Call",
            
            // Modal
            starKaryakarta: "Star Karyakarta - Indian National Congress",
            phoneNA: "N/A",
            totalPointLabel: "टोटल पोइन्ट :",
            
            // Stats labels
            survey: "सर्वे",
            sleep: "स्लीप",
            message: "मैसेज",
            photo: "फ़ोटो",
            contactUpdate: "कॉन्टेक्ट अपडेट",
            audio: "ऑडियो",
            video: "वीडियो",
            selfie: "सेल्फी",
            call: "कॉल",
            sms: "एस.एम.एस.",
            print: "प्रिन्ट"
        },
        
        hi: {
            // Page titles
            title: "स्टार स्वयंसेवक",
            subtitle: "कुल अंकों के आधार पर शीर्ष प्रदर्शन करने वाले कार्यकर्ता",
            
            // Messages
            loading: "लीडरबोर्ड लोड हो रहा है...",
            noVolunteers: "कोई स्वयंसेवक नहीं मिला।",
            
            // Card labels
            totalPoint: "कुल अंक:",
            volunteer: "स्वयंसेवक",
            call: "कॉल",
            
            // Modal
            starKaryakarta: "स्टार कार्यकर्ता - भारतीय राष्ट्रीय कांग्रेस",
            phoneNA: "N/A",
            totalPointLabel: "टोटल पोइन्ट :",
            
            // Stats labels
            survey: "सर्वे",
            sleep: "स्लीप",
            message: "मैसेज",
            photo: "फ़ोटो",
            contactUpdate: "कॉन्टेक्ट अपडेट",
            audio: "ऑडियो",
            video: "वीडियो",
            selfie: "सेल्फी",
            call: "कॉल",
            sms: "एस.एम.एस.",
            print: "प्रिन्ट"
        },
        
        gu: {
            // Page titles
            title: "સ્ટાર સ્વયંસેવક",
            subtitle: "કુલ પોઈન્ટના આધારે ટોપ પરફોર્મિંગ કાર્યકર્તાઓ",
            
            // Messages
            loading: "લીડરબોર્ડ લોડ થઈ રહ્યું છે...",
            noVolunteers: "કોઈ સ્વયંસેવક મળ્યો નથી.",
            
            // Card labels
            totalPoint: "કુલ પોઈન્ટ:",
            volunteer: "સ્વયંસેવક",
            call: "કૉલ",
            
            // Modal
            starKaryakarta: "સ્ટાર કાર્યકર્તા - ભારતીય રાષ્ટ્રીય કોંગ્રેસ",
            phoneNA: "N/A",
            totalPointLabel: "ટોટલ પોઇન્ટ :",
            
            // Stats labels
            survey: "સર્વે",
            sleep: "સ્લીપ",
            message: "મેસેજ",
            photo: "ફોટો",
            contactUpdate: "કોન્ટેક્ટ અપડેટ",
            audio: "ઓડિયો",
            video: "વિડિયો",
            selfie: "સેલ્ફી",
            call: "કૉલ",
            sms: "એસ.એમ.એસ.",
            print: "પ્રિંટ"
        }
    };

    const txt = t[lang];

    // --- Modal State ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedVol, setSelectedVol] = useState(null);
    const [modalStats, setModalStats] = useState(null);
    const [modalUserDetails, setModalUserDetails] = useState(null);

    // --- Local Storage State for Vidhan Sabha ---
    const [vsDetails, setVsDetails] = useState({ number: 'N/A', name: 'N/A' });

    // Load Vidhan Sabha from Local Storage on Mount
    useEffect(() => {
        const savedVS = localStorage.getItem("userVidhanSabha");
        if (savedVS) {
            try {
                const vsData = JSON.parse(savedVS);
                setVsDetails({
                    number: vsData.number || 'N/A',
                    name: vsData.name || 'N/A'
                });
            } catch (e) {
                console.error("Error parsing Vidhan Sabha from local storage");
            }
        }
    }, []);

    // Fetch initial list
    useEffect(() => {
        const fetchStarVolunteers = async () => {
            if (!appId) return;

            setLoading(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/dis_star_karykarta`, {
                    app_id: appId
                });

                if (response.data.success && response.data.data && response.data.data.result) {
                    const sortedVolunteers = response.data.data.result.sort((a, b) => b.total - a.total);
                    setVolunteers(sortedVolunteers);
                } else {
                    setVolunteers([]);
                }
            } catch (error) {
                console.error("Error fetching star volunteers:", error);
                setVolunteers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchStarVolunteers();
    }, [appId]);

    // Handle Card Click & Fetch Details
    const handleCardClick = async (vol, index) => {
        setSelectedVol({ ...vol, rank: index + 1 });
        setIsModalOpen(true);
        setModalLoading(true);
        setModalStats(null);
        setModalUserDetails(null); // Reset

        try {
            const response = await axios.post(`${BASE_URL}/api/view_star_karykarta_detail`, {
                app_id: appId,
                user_id: vol.user_id
            });

            if (response.data.success && response.data.data) {
                // Set right-side stats from result
                if (response.data.data.result && response.data.data.result.length > 0) {
                    setModalStats(response.data.data.result[0]);
                }
                // Set left-side detailed user info from result2
                if (response.data.data.result2 && response.data.data.result2.length > 0) {
                    setModalUserDetails(response.data.data.result2[0]);
                }
            }
        } catch (error) {
            console.error("Error fetching volunteer details:", error);
        } finally {
            setModalLoading(false);
        }
    };

    // Helper function to safely get the image URL
    const getAvatarSrc = (vol) => {
        if (vol?.photo && vol.photo.startsWith('http')) {
            return vol.photo;
        } else if (vol?.photo_path && vol.photo_path.trim() !== "") {
            return vol.photo_path;
        }
        return "https://ui-avatars.com/api/?background=e2e8f0&color=475569&name=" + encodeURIComponent(vol?.name || "User");
    };

    return (
        <div className='app-container p-4 custom-responsive-container'>
            
            {/* ADDED HEADER COMPONENT HERE */}
            <Header 
                title={txt.title}
            />

            {/* Loading State */}
            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loading}</p>
                </div>
            )}

            {!loading && volunteers.length === 0 && (
                <div className="text-center text-muted p-5">
                    {txt.noVolunteers}
                </div>
            )}

            {/* --- LEADERBOARD GRID --- */}
            <div className="row g-4 pb-5 mt-1">
                {!loading && volunteers.map((vol, index) => {
                    const isFirst = index === 0;
                    let medal = " ";
                    if (index === 0) medal = "🥇";
                    else if (index === 1) medal = "🥈";
                    else if (index === 2) medal = "🥉";

                    return (
                        <div className="col-12 col-md-6 col-lg-4" key={vol.user_id || index}>
                            <div 
                                className="leaderboard-card clickable-card"
                                onClick={() => handleCardClick(vol, index)}
                            >
                                {/* UPDATED: White Background with Dynamic Point Badge Colors */}
                                <div className="card-top">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="rank-text">
                                            {index + 1}
                                        </div>
                                        <div className={`points-badge ${isFirst ? 'points-gold' : 'points-silver'}`}>
                                            {txt.totalPoint} {vol.total || 0}
                                        </div>
                                    </div>
                                    <div className="medal-icon">
                                        {medal}
                                    </div>
                                </div>

                                {/* Bottom Section: User Details & Call Button */}
                                <div className="card-bottom">
                                    <div className="d-flex align-items-center gap-3" style={{ flex: 1, minWidth: 0 }}>
                                        <img 
                                            src={getAvatarSrc(vol)} 
                                            alt={vol.name} 
                                            className="user-avatar"
                                            onError={(e) => {
                                                e.target.src = "https://ui-avatars.com/api/?background=e2e8f0&color=475569&name=" + encodeURIComponent(vol.name);
                                            }}
                                        />
                                        <div className="user-info">
                                            <h5 className="user-name text-truncate" title={vol.name}>{vol.name}</h5>
                                            <span className="user-designation text-truncate">{vol.designation || txt.volunteer}</span>
                                        </div>
                                    </div>

                                    {vol.mobile_no && (
                                        <a 
                                            href={`tel:${vol.mobile_no}`} 
                                            className="call-btn" 
                                            title={txt.call}
                                            onClick={(e) => e.stopPropagation()} 
                                        >
                                            <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                                            </svg>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* --- DETAILED STATS MODAL --- */}
            {isModalOpen && selectedVol && (
                <div className="custom-modal-overlay" onClick={() => setIsModalOpen(false)}>
                    <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="premium-close-btn" onClick={() => setIsModalOpen(false)}>
                            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                        
                        <div className="modal-split-layout">
                            {/* LEFT SIDE: User Profile */}
                            <div className="modal-left">
                                {/* Dynamic Header Color based on Rank */}
                                <div className={selectedVol.rank === 1 ? "modal-gold-header" : "modal-silver-header"}>
                                    {txt.starKaryakarta}
                                </div>
                                <div className="modal-profile-body">
                                    <div className="avatar-wrapper">
                                        {selectedVol.rank === 1 && <span className="crown left-crown">👑</span>}
                                        <img 
                                            src={getAvatarSrc(selectedVol)} 
                                            alt={selectedVol.name} 
                                            className="modal-avatar"
                                            onError={(e) => { e.target.src = "https://ui-avatars.com/api/?background=e2e8f0&color=475569&name=" + encodeURIComponent(selectedVol.name); }}
                                        />
                                        {selectedVol.rank === 1 && <span className="crown right-crown">👑</span>}
                                    </div>
                                    <h3 className={`modal-name ${selectedVol.rank !== 1 ? 'silver-text' : ''}`}>{selectedVol.name}</h3>
                                    <p className="modal-designation">{selectedVol.designation || txt.volunteer}</p>
                                    <p className="modal-phone">{selectedVol.mobile_no || txt.phoneNA}</p>
                                </div>

                                {/* Dynamic Footer elements based on Rank */}
                                <div className="vidhan-sabha-container mt-auto">
                                    <div className="d-flex align-items-center">
                                        <div className={`bjp-logo-wrapper ${selectedVol.rank !== 1 ? 'silver-border' : ''}`}>
                                            <img src="https://maharashtra.bjpwings.com/img/admin/09022026022058_1770632573055.jpg" alt="Party Logo" />
                                        </div>
                                        <div className="vidhan-sabha-text text-truncate">
                                            {vsDetails.number} : {vsDetails.name}
                                        </div>
                                    </div>
                                    <div className={selectedVol.rank === 1 ? "gold-bottom-line" : "silver-bottom-line"}></div>
                                </div>
                            </div>

                            {/* RIGHT SIDE: Premium Blue Stats Grid */}
                            <div className="modal-right">
                                {modalLoading ? (
                                    <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                                        <div className="spinner-border text-primary" role="status"></div>
                                    </div>
                                ) : (
                                    <div className="stats-table">
                                        <div className="stats-header">
                                            {txt.totalPointLabel} {selectedVol.total || 0}
                                        </div>
                                        
                                        {/* Row 1 */}
                                        <div className="stats-row">
                                            <div className="stats-cell">
                                                <span className="cell-label">{txt.survey}</span>
                                                <span className="cell-value">{modalStats?.SURVEY || 0}</span>
                                            </div>
                                            <div className="stats-cell">
                                                <span className="cell-label">{txt.sleep}</span>
                                                <span className="cell-value">{modalStats?.SLEEP || 0}</span>
                                            </div>
                                        </div>

                                        {/* Row 2 */}
                                        <div className="stats-row">
                                            <div className="stats-cell">
                                                <span className="cell-label">{txt.message}</span>
                                                <span className="cell-value">{modalStats?.TEXT || 0}</span>
                                            </div>
                                            <div className="stats-cell">
                                                <span className="cell-label">{txt.photo}</span>
                                                <span className="cell-value">{modalStats?.IMAGE || 0}</span>
                                            </div>
                                            <div className="stats-cell">
                                                <span className="cell-label">{txt.contactUpdate}</span>
                                                <span className="cell-value">{modalStats?.["MOBILE UPDATE"] || 0}</span>
                                            </div>
                                        </div>

                                        {/* Row 3 */}
                                        <div className="stats-row">
                                            <div className="stats-cell">
                                                <span className="cell-label">{txt.audio}</span>
                                                <span className="cell-value">{modalStats?.AUDIO || 0}</span>
                                            </div>
                                            <div className="stats-cell">
                                                <span className="cell-label">{txt.video}</span>
                                                <span className="cell-value">{modalStats?.VIDEO || 0}</span>
                                            </div>
                                            <div className="stats-cell">
                                                <span className="cell-label">{txt.selfie}</span>
                                                <span className="cell-value">{modalStats?.SELFIE || 0}</span>
                                            </div>
                                        </div>

                                        {/* Row 4 */}
                                        <div className="stats-row" style={{ borderBottom: 'none' }}>
                                            <div className="stats-cell">
                                                <span className="cell-label">{txt.call}</span>
                                                <span className="cell-value">{modalStats?.CALL || 0}</span>
                                            </div>
                                            <div className="stats-cell">
                                                <span className="cell-label">{txt.sms}</span>
                                                <span className="cell-value">{modalStats?.["SMS SLEEP"] || 0}</span>
                                            </div>
                                            <div className="stats-cell">
                                                <span className="cell-label">{txt.print}</span>
                                                <span className="cell-value">{modalStats?.PRINT || 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; }
                .loader-text { color: #6B7280; font-weight: 500; font-size: 1.1rem; animation: pulse 1.5s ease-in-out infinite; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

                .clickable-card { cursor: pointer; }
                .leaderboard-card {
                    background: #ffffff; border-radius: 20px; box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.05);
                    border: 1px solid #f1f5f9; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    display: flex; flex-direction: column; overflow: hidden; height: 100%;
                }
                .leaderboard-card:hover { transform: translateY(-6px); box-shadow: 0 20px 40px -5px rgba(0, 0, 0, 0.1); }

                /* UPDATED: Clean White Top Section */
                .card-top { 
                    padding: 16px 24px; 
                    display: flex; 
                    justify-content: space-between; 
                    align-items: center; 
                    background: #ffffff;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .rank-text { 
                    font-size: 1.25rem; 
                    font-weight: 800; 
                    letter-spacing: 0.5px; 
                    color: #334155;
                }

                /* UPDATED: Dynamic Badge Styles */
                .points-badge { 
                    font-size: 1.05rem; 
                    font-weight: 700; 
                    padding: 6px 14px; 
                    border-radius: 50px; 
                }
                .points-gold {
                    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%); 
                    color: #ffffff;
                    box-shadow: 0 4px 10px rgba(245, 158, 11, 0.3);
                }
                .points-silver {
                    background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%);
                    color: #ffffff;
                    box-shadow: 0 4px 10px rgba(100, 116, 139, 0.3);
                }

                .medal-icon { font-size: 2.2rem; line-height: 1; filter: drop-shadow(0 4px 6px rgba(0,0,0,0.15)); }

                .card-bottom { display: flex; align-items: center; justify-content: space-between; padding: 24px; background: #ffffff; gap: 16px; }
                .user-avatar { width: 64px; height: 64px; border-radius: 50%; object-fit: cover; background-color: #f8fafc; border: 3px solid #ffffff; box-shadow: 0 4px 10px rgba(0,0,0,0.08); flex-shrink: 0; }
                .user-info { display: flex; flex-direction: column; overflow: hidden; }
                .user-name { margin: 0; font-size: 1.15rem; font-weight: 800; color: #0f172a; line-height: 1.2; }
                .user-designation { font-size: 0.8rem; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 6px; }

                .call-btn {
                    display: flex; align-items: center; justify-content: center; width: 48px; height: 48px;
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: #ffffff; border-radius: 50%;
                    text-decoration: none; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); flex-shrink: 0;
                    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.3); 
                }
                .call-btn:hover { transform: scale(1.1) translateY(-2px); box-shadow: 0 8px 20px rgba(16, 185, 129, 0.4); color: #ffffff; }

                /* =====================================
                   ENHANCED MODAL STYLES 
                   ===================================== */
                .custom-modal-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15, 23, 42, 0.7); 
                    z-index: 9999; display: flex; justify-content: center; align-items: center;
                    backdrop-filter: blur(8px); 
                    padding: 20px;
                }
                .custom-modal-content {
                    background: #ffffff; 
                    border-radius: 24px; 
                    width: 100%; max-width: 850px;
                    position: relative; overflow: hidden;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5); 
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    animation: modalFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    max-height: 90vh;
                    overflow-y: auto;
                }
                @keyframes modalFadeIn {
                    from { opacity: 0; transform: scale(0.95) translateY(15px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }

                .premium-close-btn {
                    position: absolute; top: 16px; right: 16px;
                    width: 36px; height: 36px;
                    background: rgba(148, 163, 184, 0.15);
                    border: none; border-radius: 50%;
                    color: #475569; display: flex; align-items: center; justify-content: center;
                    cursor: pointer; z-index: 10;
                    transition: all 0.2s;
                }
                .premium-close-btn:hover {
                    background: #ef4444; color: #ffffff;
                    transform: rotate(90deg);
                }

                .modal-split-layout { display: flex; flex-direction: row; min-height: 420px; }

                /* --- Modal Left Side (Profile) --- */
                .modal-left {
                    width: 42%; background: #ffffff; border-right: 1px solid #f1f5f9;
                    display: flex; flex-direction: column; position: relative;
                }
                .modal-gold-header {
                    background: linear-gradient(135deg, #b45309 0%, #d97706 50%, #f59e0b 100%); 
                    color: #ffffff; padding: 18px 16px; text-align: center;
                    font-weight: 800; font-size: 1.05rem; letter-spacing: 0.5px;
                }
                .modal-silver-header {
                    background: linear-gradient(135deg, #94a3b8 0%, #64748b 50%, #475569 100%);
                    color: #ffffff; padding: 18px 16px; text-align: center;
                    font-weight: 800; font-size: 1.05rem; letter-spacing: 0.5px;
                }

                .modal-profile-body {
                    padding: 40px 20px 20px 20px; display: flex; flex-direction: column;
                    align-items: center; text-align: center; flex-grow: 1;
                }
                .avatar-wrapper { position: relative; margin-bottom: 20px; }
                .modal-avatar {
                    width: 120px; height: 120px; border-radius: 50%; object-fit: cover;
                    border: 4px solid #ffffff; background: #f8fafc;
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1); 
                }
                .crown { position: absolute; font-size: 28px; top: -12px; filter: drop-shadow(0 4px 6px rgba(0,0,0,0.3)); }
                .left-crown { left: -18px; transform: rotate(-20deg); }
                .right-crown { right: -18px; transform: rotate(20deg); }

                .modal-name { color: #d97706; font-weight: 800; font-size: 1.5rem; margin: 0 0 6px 0; }
                .modal-name.silver-text { color: #475569; }
                .modal-designation { color: #334155; font-weight: 800; font-size: 1rem; margin: 0 0 8px 0; letter-spacing: 0.3px; }
                .modal-phone { color: #2563eb; font-weight: 700; margin: 0; font-size: 1.1rem; }

                /* Vidhan Sabha Footer Section */
                .vidhan-sabha-container {
                    padding: 20px 24px 28px 24px; 
                    background: #ffffff; position: relative; border-top: 1px solid #f1f5f9;
                    width: 100%;
                }
                .bjp-logo-wrapper {
                    width: 52px; height: 52px; border-radius: 50%;
                    background: radial-gradient(circle, #f7fee7 0%, #fef3c7 100%);
                    border: 3px solid #d97706; display: flex; align-items: center; justify-content: center;
                    box-shadow: 0 4px 10px rgba(217, 119, 6, 0.2); flex-shrink: 0;
                    overflow: hidden;
                    
                }
                .bjp-logo-wrapper.silver-border {
                    border-color: #94a3b8;
                    background: radial-gradient(circle, #f8fafc 0%, #e2e8f0 100%);
                    box-shadow: 0 4px 10px rgba(100, 116, 139, 0.2);
                }
                .bjp-logo-wrapper img { width: 55px; height: 55px; border-radius: 50%; object-fit: cover; background-color: #f8fafc; border: 3px solid #ffffff; box-shadow: 0 4px 10px rgba(0,0,0,0.08); flex-shrink: 0;}
                
                .vidhan-sabha-text { 
                    color: #ea580c; font-size: 1.2rem; font-weight: 900; 
                    margin-left: 15px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
                }
                
                .gold-bottom-line {
                    position: absolute; bottom: 0; left: 0; width: 100%; height: 8px;
                    background: linear-gradient(90deg, #b45309 0%, #fcd34d 50%, #b45309 100%);
                }
                .silver-bottom-line {
                    position: absolute; bottom: 0; left: 0; width: 100%; height: 8px;
                    background: linear-gradient(90deg, #64748b 0%, #cbd5e1 50%, #64748b 100%);
                }

                /* --- Modal Right Side (Premium Stats Grid) --- */
                .modal-right {
                    width: 58%; padding: 32px; display: flex; align-items: center;
                    justify-content: center; background-color: #f8fafc;
                }
                .stats-table {
                    background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%); 
                    color: #ffffff; border: 1px solid #334155;
                    width: 100%; border-radius: 16px; overflow: hidden;
                    box-shadow: 0 15px 35px rgba(0,0,0,0.15);
                }
                .stats-header {
                    color: #fbbf24; font-weight: 800; text-align: center;
                    padding: 16px; border-bottom: 1px solid rgba(255,255,255,0.1); 
                    font-size: 1.25rem; background: rgba(255,255,255,0.03);
                    letter-spacing: 0.5px;
                }
                .stats-row { display: flex; border-bottom: 1px solid rgba(255,255,255,0.06); }
                .stats-cell {
                    flex: 1; text-align: center; padding: 20px 10px; 
                    border-right: 1px solid rgba(255,255,255,0.06);
                    display: flex; flex-direction: column; gap: 8px;
                    transition: background 0.2s ease;
                }
                .stats-cell:hover { background: rgba(255,255,255,0.05); } 
                .stats-cell:last-child { border-right: none; }
                
                .cell-label { 
                    font-size: 0.85rem; font-weight: 600; color: #94a3b8; 
                    text-transform: uppercase; letter-spacing: 0.5px; 
                }
                .cell-value { font-size: 1.4rem; font-weight: 900; color: #ffffff; }

                /* =====================================
                   RESPONSIVE MEDIA QUERIES 
                   ===================================== */
                @media (max-width: 992px) {
                    .modal-left { width: 45%; }
                    .modal-right { width: 55%; padding: 20px; }
                    .stats-cell { padding: 15px 8px; gap: 4px; }
                    .cell-value { font-size: 1.2rem; }
                    .cell-label { font-size: 0.75rem; }
                }

                @media (max-width: 768px) {
                    .custom-responsive-container { padding: 10px !important; }
                    .card-bottom { padding: 16px; }
                    .user-avatar { width: 50px; height: 50px; }
                    .call-btn { width: 40px; height: 40px; }
                    .medal-icon { font-size: 1.8rem; }
                    
                    /* Modal Stacking for Mobile */
                    .modal-split-layout { flex-direction: column; min-height: auto; }
                    .modal-left { width: 100%; border-right: none; border-bottom: 1px solid #e2e8f0; }
                    .modal-right { width: 100%; padding: 20px 15px; }
                    
                    .modal-avatar { width: 100px; height: 100px; }
                    .modal-name { font-size: 1.3rem; }
                    .vidhan-sabha-container { padding: 15px 20px 20px 20px; }
                    .vidhan-sabha-text { font-size: 1.1rem; }
                    
                    .premium-close-btn { 
                        top: 10px; right: 10px; 
                        width: 30px; height: 30px; 
                        background: rgba(0,0,0,0.5); /* Make more visible on mobile */
                        color: white;
                    }
                }

                @media (max-width: 480px) {
                    .stats-header { font-size: 1.1rem; padding: 12px; }
                    .stats-cell { padding: 12px 4px; }
                    .cell-value { font-size: 1.1rem; }
                    .cell-label { font-size: 0.65rem; letter-spacing: 0; }
                    .rank-text { font-size: 1.1rem; }
                    .points-badge { font-size: 0.95rem; }
                }
            `}</style>
        </div>
    );
};

export default StarVolunteer;